import React, { useState } from "react";
import { HiMenu } from "react-icons/hi";
import { IoClose, IoChevronDown } from "react-icons/io5";
import { Link, useLocation } from "react-router-dom";
import LanguageSwitch from "./LanguageSwitch";
import UserMenu from "../user/UserMenu";
import routePaths from "../../routes/routePaths";

const HeaderMobile = ({ menuItems, user, avatar, userRoles = "" }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const toggleSubMenu = (label) => {
    setOpenSubMenu((prev) => (prev === label ? null : label));
  };

  // Helper: kiểm tra active cho subMenu
  const isSubActive = (subs) => subs?.some((s) => currentPath === s.to);

  return (
    <div className="block md:hidden relative z-50">
      {/* Header Bar */}
      <div className="flex items-center h-14 px-2 bg-card border-b border-border">
        {/* Hamburger trái */}
        <button
          onClick={() => setIsMenuOpen(true)}
          className="p-2 flex-shrink-0 text-primary"
          aria-label="Mở menu"
        >
          <HiMenu className="h-7 w-7" />
        </button>
        {/* Logo giữa */}
        <Link to={routePaths.home} className="mx-auto">
          <img
            src="/logo.png"
            alt="Logo"
            className="h-8 rounded bg-white"
          />
        </Link>
        {/* Actions phải */}
        <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
          <LanguageSwitch isMobile />
          <UserMenu user={user} avatar={avatar} userRoles={userRoles} isMobile />
        </div>
      </div>

      {/* Offcanvas menu */}
      <div className={`fixed inset-0 z-[100] ${isMenuOpen ? '' : 'pointer-events-none'}`}>
        {/* Overlay: fade + blur */}
        <div
          className={`
            absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300
            ${isMenuOpen ? 'opacity-100' : 'opacity-0'}
          `}
          onClick={() => setIsMenuOpen(false)}
        />
        {/* Slide panel: rounded, shadow, smooth */}
        <aside
          className={`
            fixed top-0 left-0 h-full w-72 max-w-full bg-card rounded-r shadow border-r-2 border-primary z-[110]
            transition-transform duration-300 ease-in-out
            ${isMenuOpen ? "translate-x-0" : "-translate-x-full"}
            flex flex-col
          `}
          style={{ willChange: "transform" }}
        >
          {/* Logo lớn */}
          <div className="flex items-center mt-4 mb-3 pl-5">
            <Link to={routePaths.home} onClick={() => setIsMenuOpen(false)}>
              <img
                src="/logo.png"
                alt="Quyenxd English Class"
                className="h-10 w-auto rounded bg-white"
              />
            </Link>
          </div>
          {/* Đóng menu */}
          <button
            onClick={() => setIsMenuOpen(false)}
            className="absolute top-3 right-3 p-2 text-primary hover:bg-card rounded transition"
            aria-label="Đóng menu"
          >
            <IoClose className="h-7 w-7" />
          </button>
          {/* Danh sách menu */}
          <nav className="mt-4 flex-1 flex flex-col gap-1 px-5 pb-6 overflow-y-auto">
            {menuItems.map((item) => (
              <div key={item.to || item.label} className="relative">
                {!item.subMenuItems ? (
                  <Link
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className={`
                      group block py-3 border-b border-border text-base rounded px-2 font-semibold transition
                      ${currentPath === item.to
                        ? "bg-primary/10 text-primary"
                        : "text-menu hover:text-primary hover:bg-primary/5"}
                      relative
                    `}
                  >
                    {/* Active bar */}
                    {currentPath === item.to && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1 bg-primary rounded-r"></span>
                    )}
                    {item.label}
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => toggleSubMenu(item.label)}
                      className={`
                        w-full flex items-center justify-between py-3 border-b border-border text-base font-semibold px-2 rounded transition
                        ${openSubMenu === item.label || isSubActive(item.subMenuItems)
                          ? "bg-primary/20 text-primary"
                          : "text-menu hover:text-primary hover:bg-primary/5"}
                        relative
                      `}
                    >
                      {/* Active bar cho subMenu */}
                      {(openSubMenu === item.label || isSubActive(item.subMenuItems)) && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 h-7 w-1 bg-primary rounded-r"></span>
                      )}
                      <span>{item.label}</span>
                      {/* Mũi tên động */}
                      <IoChevronDown
                        className={`
                          w-5 h-5 ml-2 transition-transform duration-200
                          ${openSubMenu === item.label ? "rotate-180" : ""}
                        `}
                      />
                    </button>
                    {/* Submenu: expand/collapse mượt */}
                    <div
                      className={`
                        transition-all duration-300 overflow-hidden
                        ${openSubMenu === item.label
                          ? "max-h-40 opacity-100 mt-0"
                          : "max-h-0 opacity-0 -mt-2"}
                        pl-4
                      `}
                    >
                      {item.subMenuItems.map((sub) => (
                        <Link
                          key={sub.to}
                          to={sub.to}
                          onClick={() => setIsMenuOpen(false)}
                          className={`
                            block py-2 text-sm rounded px-2 font-medium transition
                            ${currentPath === sub.to
                              ? "bg-primary/10 text-primary"
                              : "text-menu hover:bg-primary/10 hover:text-primary"}
                            relative
                          `}
                        >
                          {/* Dot active cho sub */}
                          {currentPath === sub.to && (
                            <span className="absolute left-0 top-1/2 -translate-y-1/2 h-3 w-3 bg-primary rounded-full"></span>
                          )}
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ))}
          </nav>
          {/* Actions (language, user) */}
          <div className="flex flex-col items-center gap-2 mt-auto mb-6">
            <LanguageSwitch isMobile />
            <UserMenu user={user} avatar={avatar} userRoles={userRoles} isMobile />
          </div>
        </aside>
      </div>
    </div>
  );
};

export default HeaderMobile;
